import { useState, useEffect } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Select, SelectItem, Divider,
} from '@heroui/react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLineItem, useCreateLineItem, useUpdateLineItem, useAddLineItemHardware, useRemoveLineItemHardware } from '@/api/line-items';
import { useProducts } from '@/api/products';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useFinishes } from '@/api/finishes';
import { useConstructionMethods } from '@/api/construction-methods';
import { useHardwareLibrary } from '@/api/hardware-library';
import type { CreateLineItem } from '@estimator/shared';
import { toTemplatePayload } from '@/utils/line-item-template';

interface LineItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  editingId: string | null;
  templateMode?: boolean;
}

const EMPTY_FORM: CreateLineItem = {
  area_id: '',
  template_id: null,
  cabinet_code: null,
  description: '',
  quantity: 1,
  width: null,
  height: null,
  depth: null,
  num_doors: 0,
  num_drawers: 0,
  num_shelves: 0,
  box_material_id: null,
  door_material_id: null,
  back_material_id: null,
  shelf_material_id: null,
  finish_id: null,
  finish_sqft_override: null,
  construction_method_id: null,
  construction_overrides: '{}',
  position_x: 0,
  position_y: 0,
  position_z: 0,
  is_in_3d_view: false,
  material_overrides: '{}',
  notes: null,
  sort_order: 0,
  exclude_labor: false,
  is_template: false,
};

interface HwRow {
  id?: string;
  hardware_id: string;
  quantity: number;
}

export default function LineItemModal({ isOpen, onClose, areaId, editingId, templateMode = false }: LineItemModalProps) {
  const [form, setForm] = useState<CreateLineItem>({ ...EMPTY_FORM, area_id: areaId });
  const [hwRows, setHwRows] = useState<HwRow[]>([]);

  const { data: editingItem } = useLineItem(editingId);
  const { data: templates = [] } = useProducts();
  const { data: materials = [] } = useMaterialsLibrary();
  const { data: finishes = [] } = useFinishes();
  const { data: methods = [] } = useConstructionMethods();
  const { data: hardwareList = [] } = useHardwareLibrary();

  const createMutation = useCreateLineItem();
  const updateMutation = useUpdateLineItem();
  const addHwMutation = useAddLineItemHardware();
  const removeHwMutation = useRemoveLineItemHardware();

  // Populate form when editing
  useEffect(() => {
    if (editingId && editingItem) {
      const item = editingItem as any;
      setForm({
        area_id: item.area_id,
        template_id: item.template_id ?? null,
        cabinet_code: item.cabinet_code ?? null,
        description: item.description ?? '',
        quantity: item.quantity ?? 1,
        width: item.width ?? null,
        height: item.height ?? null,
        depth: item.depth ?? null,
        num_doors: item.num_doors ?? 0,
        num_drawers: item.num_drawers ?? 0,
        num_shelves: item.num_shelves ?? 0,
        box_material_id: item.box_material_id ?? null,
        door_material_id: item.door_material_id ?? null,
        back_material_id: item.back_material_id ?? null,
        shelf_material_id: item.shelf_material_id ?? null,
        finish_id: item.finish_id ?? null,
        finish_sqft_override: item.finish_sqft_override ?? null,
        construction_method_id: item.construction_method_id ?? null,
        construction_overrides: item.construction_overrides ?? '{}',
        position_x: item.position_x ?? 0,
        position_y: item.position_y ?? 0,
        position_z: item.position_z ?? 0,
        is_in_3d_view: item.is_in_3d_view ?? false,
        material_overrides: item.material_overrides ?? '{}',
        notes: item.notes ?? null,
        sort_order: item.sort_order ?? 0,
        exclude_labor: item.exclude_labor ?? false,
        is_template: item.is_template ?? false,
      });
      // Load hardware rows
      if (item.hardware && Array.isArray(item.hardware)) {
        setHwRows(item.hardware.map((h: any) => ({
          id: h.id,
          hardware_id: h.hardware_id,
          quantity: h.quantity,
        })));
      }
    } else {
      setForm({ ...EMPTY_FORM, area_id: areaId });
      setHwRows([]);
    }
  }, [editingId, editingItem, areaId]);

  // Reset form when opening for new item
  useEffect(() => {
    if (isOpen && !editingId) {
      setForm({ ...EMPTY_FORM, area_id: areaId });
      setHwRows([]);
    }
  }, [isOpen, editingId, areaId]);

  const updateField = <K extends keyof CreateLineItem>(key: K, value: CreateLineItem[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Template selection auto-populates fields
  const handleTemplateSelect = (templateId: string) => {
    const tmpl = (templates as any[]).find((t) => t.id === templateId);
    if (!tmpl) {
      updateField('template_id', null);
      return;
    }
    setForm((prev) => ({
      ...prev,
      template_id: templateId,
      cabinet_code: tmpl.code || prev.cabinet_code,
      description: tmpl.name || prev.description,
      width: tmpl.default_width ?? prev.width,
      height: tmpl.default_height ?? prev.height,
      depth: tmpl.default_depth ?? prev.depth,
      num_doors: tmpl.default_doors ?? prev.num_doors,
      num_drawers: tmpl.default_drawers ?? prev.num_drawers,
      num_shelves: tmpl.default_shelves ?? prev.num_shelves,
      construction_method_id: tmpl.construction_method_id ?? prev.construction_method_id,
    }));
    // Auto-populate hardware from template
    if (tmpl.template_hardware && Array.isArray(tmpl.template_hardware)) {
      setHwRows(tmpl.template_hardware.map((th: any) => ({
        hardware_id: th.hardware_id,
        quantity: th.quantity_per_cabinet ?? 1,
      })));
    }
  };

  const handleSave = async () => {
    if (!form.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    try {
      if (editingId) {
        const { area_id: _areaId, ...updateData } = form;
        await updateMutation.mutateAsync({ id: editingId, ...updateData });
        // Sync hardware: remove old, add new
        const existingHw = hwRows.filter((r) => r.id);
        const newHw = hwRows.filter((r) => !r.id);
        const originalIds = (editingItem as any)?.hardware?.map((h: any) => h.id) || [];
        const keptIds = existingHw.map((r) => r.id!);
        const removedIds = originalIds.filter((id: string) => !keptIds.includes(id));
        for (const hwId of removedIds) {
          await removeHwMutation.mutateAsync({ lineItemId: editingId, hwId });
        }
        for (const row of newHw) {
          if (row.hardware_id) {
            await addHwMutation.mutateAsync({
              lineItemId: editingId,
              hardware_id: row.hardware_id,
              quantity: row.quantity,
            });
          }
        }
        toast.success('Line item updated');
      } else {
        const payload = templateMode ? toTemplatePayload(form) : form;
        const saved = await createMutation.mutateAsync(payload);
        // Hardware is a relation — never create it for a template row.
        if (!templateMode && !form.template_id && hwRows.length > 0) {
          for (const row of hwRows) {
            if (row.hardware_id) {
              await addHwMutation.mutateAsync({
                lineItemId: (saved as any).id,
                hardware_id: row.hardware_id,
                quantity: row.quantity,
              });
            }
          }
        }
        toast.success(templateMode ? 'Template created' : 'Line item created');
      }
      onClose();
    } catch {
      toast.error('Failed to save line item');
    }
  };

  const addHwRow = () => setHwRows((prev) => [...prev, { hardware_id: '', quantity: 1 }]);
  const removeHwRow = (index: number) => setHwRows((prev) => prev.filter((_, i) => i !== index));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)] text-[var(--text-primary)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader>{editingId ? 'Edit Line Item' : 'New Line Item'}</ModalHeader>
        <ModalBody className="gap-4">
          {/* Template Selection */}
          <Select
            label="Template (optional)"
            selectedKeys={form.template_id ? [form.template_id] : []}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as string;
              if (val) handleTemplateSelect(val);
              else updateField('template_id', null);
            }}
            size="sm"
            classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
          >
            {(templates as any[]).map((t) => (
              <SelectItem key={t.id}>{`${t.code} — ${t.name}`}</SelectItem>
            ))}
          </Select>

          <Divider />

          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-3">
            <Input label="Code" size="sm" value={form.cabinet_code ?? ''} onValueChange={(v) => updateField('cabinet_code', v || null)} />
            <div className="col-span-2">
              <Input label="Description *" size="sm" value={form.description} onValueChange={(v) => updateField('description', v)} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Input label="Quantity" type="number" size="sm" value={String(form.quantity)} onValueChange={(v) => updateField('quantity', parseInt(v) || 1)} />
            <Input label="Width (in)" type="number" size="sm" value={form.width != null ? String(form.width) : ''} onValueChange={(v) => updateField('width', v ? parseFloat(v) : null)} />
            <Input label="Height (in)" type="number" size="sm" value={form.height != null ? String(form.height) : ''} onValueChange={(v) => updateField('height', v ? parseFloat(v) : null)} />
            <Input label="Depth (in)" type="number" size="sm" value={form.depth != null ? String(form.depth) : ''} onValueChange={(v) => updateField('depth', v ? parseFloat(v) : null)} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Input label="Doors" type="number" size="sm" value={String(form.num_doors)} onValueChange={(v) => updateField('num_doors', parseInt(v) || 0)} />
            <Input label="Drawers" type="number" size="sm" value={String(form.num_drawers)} onValueChange={(v) => updateField('num_drawers', parseInt(v) || 0)} />
            <Input label="Shelves" type="number" size="sm" value={String(form.num_shelves)} onValueChange={(v) => updateField('num_shelves', parseInt(v) || 0)} />
          </div>

          <Divider />

          {/* Construction Method */}
          <Select
            label="Construction Method"
            selectedKeys={form.construction_method_id ? [form.construction_method_id] : []}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as string;
              updateField('construction_method_id', val ?? null);
            }}
            size="sm"
            classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
          >
            {(methods as any[]).map((m) => (
              <SelectItem key={m.id}>{m.name}</SelectItem>
            ))}
          </Select>

          <Divider />

          {/* Materials */}
          <div className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Materials</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Box Material', field: 'box_material_id' as const },
              { label: 'Door Material', field: 'door_material_id' as const },
              { label: 'Back Material', field: 'back_material_id' as const },
              { label: 'Shelf Material', field: 'shelf_material_id' as const },
            ].map(({ label, field }) => (
              <Select
                key={field}
                label={label}
                selectedKeys={form[field] ? [form[field] as string] : []}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0] as string;
                  updateField(field, val ?? null);
                }}
                size="sm"
                classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
              >
                {(materials as any[]).map((m) => (
                  <SelectItem key={m.id}>{m.name}</SelectItem>
                ))}
              </Select>
            ))}
          </div>

          <Divider />

          {/* Finish */}
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Finish"
              selectedKeys={form.finish_id ? [form.finish_id] : []}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as string;
                updateField('finish_id', val ?? null);
              }}
              size="sm"
              classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
            >
              {(finishes as any[]).map((f) => (
                <SelectItem key={f.id}>{f.name}</SelectItem>
              ))}
            </Select>
            <Input
              label="Finish Sq Ft Override"
              type="number"
              size="sm"
              value={form.finish_sqft_override != null ? String(form.finish_sqft_override) : ''}
              onValueChange={(v) => updateField('finish_sqft_override', v ? parseFloat(v) : null)}
            />
          </div>

          <Divider />

          {/* Hardware */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Hardware</span>
            <Button size="sm" variant="light" onPress={addHwRow} startContent={<Plus size={13} />}>
              Add
            </Button>
          </div>
          {hwRows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Select
                aria-label="Hardware"
                selectedKeys={row.hardware_id ? [row.hardware_id] : []}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0] as string;
                  setHwRows((prev) => prev.map((r, i) => i === idx ? { ...r, hardware_id: val } : r));
                }}
                size="sm"
                className="flex-1"
                classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
              >
                {(hardwareList as any[]).map((h) => (
                  <SelectItem key={h.id}>{h.name}</SelectItem>
                ))}
              </Select>
              <Input
                aria-label="Qty"
                type="number"
                size="sm"
                className="w-20"
                value={String(row.quantity)}
                onValueChange={(v) => {
                  setHwRows((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: parseInt(v) || 1 } : r));
                }}
              />
              <button onClick={() => removeHwRow(idx)} className="p-1 rounded hover:bg-[var(--bg-hover)]">
                <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
          ))}

          <Divider />

          {/* Notes */}
          <Input
            label="Notes"
            size="sm"
            value={form.notes ?? ''}
            onValueChange={(v) => updateField('notes', v || null)}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="bordered" onPress={onClose} className="border-[var(--border)] text-[var(--text-secondary)]">
            Cancel
          </Button>
          <Button color="primary" onPress={handleSave} isLoading={createMutation.isPending || updateMutation.isPending}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
